import {
  TrustAccountProvider,
  useCurrentTrustAccount,
  useTrustAccountPermissions
} from '@/features/trust-accounts/providers';
import {TrustAccountShareControl} from '@/features/trust-accounts/share';
import {TrustAccountPageLayout} from '@/features/trust-accounts/view/components';
import {PageContent} from '@/layouts';

import {
  ActivationCard,
  ActivationGlobalCta,
  ActivationPackageList,
  ActivationPageHeader,
  ActivationPageHeaderSection
} from '../components';
import {ACTIVATION_PACKAGE_CONFIG} from '../config';
import {useActivationFlow} from '../hooks';
import {ActivationPackageFormModal, SubmitTrustAccountModal} from '../modals';
import {resolveActivationGates} from '../utils';

import type {ActivationPackageViewModel} from '../hooks';
import type {TrustAccountDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface TrustAccountActivationProps {
  trustAccount: TrustAccountDTO['trustAccount'];
  packages: ActivationPackageViewModel[];
  canSendPackages: boolean;
  /** Locked purely by trust-account lifecycle status (already submitted, pending, etc.). */
  isActivationLocked: boolean;
  isTrustActive: boolean;
  error?: unknown;
  refetch?: () => void;
}

export const TrustAccountActivation: FC<TrustAccountActivationProps> = ({
  trustAccount,
  packages,
  canSendPackages,
  isActivationLocked,
  isTrustActive,
  error,
  refetch
}) => {
  return (
    <TrustAccountProvider account={trustAccount}>
      <TrustAccountPageLayout error={error} refetch={refetch}>
        <ActivationPageHeaderSection
          trustAccount={trustAccount}
          action={
            <TrustAccountShareControl
              trustAccountId={trustAccount.id}
              accountName={trustAccount.name}
            />
          }
        />
        <ActivationBody
          packages={packages}
          canSendPackages={canSendPackages}
          isActivationLocked={isActivationLocked}
          isTrustActive={isTrustActive}
        />
      </TrustAccountPageLayout>
    </TrustAccountProvider>
  );
};

interface ActivationBodyProps {
  packages: ActivationPackageViewModel[];
  canSendPackages: boolean;
  isActivationLocked: boolean;
  isTrustActive: boolean;
}

/** Split from `TrustAccountActivation` only because it must render inside `TrustAccountProvider` to read the account id and RBAC permission from context. */
const ActivationBody: FC<ActivationBodyProps> = ({
  packages,
  canSendPackages,
  isActivationLocked,
  isTrustActive
}) => {
  const {id: trustAccountId} = useCurrentTrustAccount();
  const {canUpdateAccount, canSubmitForReview} = useTrustAccountPermissions();
  const {showsGlobalCta} = resolveActivationGates({
    isActivationLocked,
    isTrustActive,
    canSendPackages,
    canUpdateAccount,
    canSubmitForReview
  });

  const {
    hasProgress,
    isSubmitting,
    handlePackageNavigate,
    handleGlobalCta,
    flowModal,
    packageFormModal
  } = useActivationFlow({trustAccountId, packages, canSendPackages, isTrustActive});

  return (
    <>
      <PageContent>
        <ActivationCard>
          <ActivationPageHeader packages={packages} />
          <ActivationPackageList
            packages={packages}
            packageConfig={ACTIVATION_PACKAGE_CONFIG}
            onPackageNavigate={handlePackageNavigate}
            isActivationLocked={isActivationLocked}
            isViewOnly={!canUpdateAccount}
          />
          <ActivationGlobalCta
            isVisible={showsGlobalCta}
            onClick={handleGlobalCta}
            isReadyToSubmit={canSendPackages}
            hasProgress={hasProgress}
            isLoading={isSubmitting}
            isTrustActive={isTrustActive}
          />
        </ActivationCard>
      </PageContent>

      <SubmitTrustAccountModal
        isOpen={flowModal.isOpen}
        state={flowModal.isActivationSuccess ? 'success' : 'confirm'}
        onConfirm={flowModal.onConfirm}
        onClose={flowModal.onClose}
        isLoading={isSubmitting}
      />

      <ActivationPackageFormModal
        trustAccountId={trustAccountId}
        isOpen={packageFormModal.isOpen}
        onClose={packageFormModal.onClose}
        initialTab={packageFormModal.initialTab}
        initialStep={packageFormModal.initialStep}
        packages={packages}
        isViewOnly={!canUpdateAccount}
        isActivationLocked={isActivationLocked}
        showErrors={hasProgress}
      />
    </>
  );
};
